import type { ReactNode } from "react";

import {
	PopoverSelect,
	type PopoverSelectOption,
} from "#/components/ui/popover-select.tsx";
import { cn } from "#/lib/utils";

export type TradePopoverSelectOption<T extends string> = PopoverSelectOption<T>;

type TradePopoverSelectProps<T extends string> = {
	align?: "center" | "start" | "end";
	ariaLabel: string;
	contentClassName?: string;
	itemClassName?: string;
	matchTriggerWidth?: boolean;
	onValueChange: (nextValue: T) => void;
	options: ReadonlyArray<TradePopoverSelectOption<T>>;
	showValue?: boolean;
	sideOffset?: number;
	triggerClassName?: string;
	triggerLabel?: ReactNode;
	value: T;
};

const contentBaseClassName =
	"rounded-xl border border-trade-border-subtle shadow-[var(--trade-shadow-popover)] dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)]";

const itemBaseClassName =
	"rounded-lg px-3 py-1.5 text-label1-bold text-trade-text hover:border-transparent hover:!bg-trade-surface-inset hover:!text-trade-text dark:hover:!bg-[var(--color-theme-fill-strong)] data-[state=checked]:text-[var(--trade-accent)] data-[state=checked]:hover:!text-[var(--trade-accent)]";

export function TradePopoverSelect<T extends string>({
	align = "center",
	ariaLabel,
	contentClassName,
	itemClassName,
	matchTriggerWidth = false,
	onValueChange,
	options,
	showValue = true,
	sideOffset = 8,
	triggerClassName,
	triggerLabel,
	value,
}: TradePopoverSelectProps<T>) {
	return (
		<PopoverSelect
			align={align}
			ariaLabel={ariaLabel}
			contentClassName={cn(contentBaseClassName, contentClassName)}
			itemClassName={cn(itemBaseClassName, itemClassName)}
			matchTriggerWidth={matchTriggerWidth}
			onValueChange={onValueChange}
			options={options}
			showValue={showValue}
			sideOffset={sideOffset}
			triggerClassName={triggerClassName}
			triggerLabel={triggerLabel}
			value={value}
		/>
	);
}
